import React, { ErrorInfo, useCallback, useMemo } from "react";
import { trackAppInsightsException } from "../../../AnalyticsTracking/app-insights-telemetry";
import { getOrCreateBookingSessionId } from "../../../AnalyticsTracking/booking-session-id";
import { ErrorBoundary } from "../components/shared/errorBoundary";
import { ThrowError } from "../components/shared/throwError";
import { ProvetContextProvider, TranslationContextProvider } from "../context";
import { FormSelectItem, ProvetTranslations } from "../types";
import { ProvetForm } from "./provetForm";

interface ProvetLayoutProps {
  activeProvetDepartmentIds: number[];
  advancedTrackingEnabled: boolean;
  /** Whether `?testErrorBoundary=true` may crash the form on purpose. False in production. */
  allowTestErrorBoundary?: boolean;
  antiForgeryToken?: string;
  bookingInformation?: string;
  defaultLatitude: number;
  defaultLongitude: number;
  defaultSpecies?: FormSelectItem[];
  defaultZoom: number;
  emergencyClinicsUrl?: string;
  excludedProvetDepartmentIds: number[];
  mapboxAccessToken?: string;
  language: string;
  oldBookingDomain?: string;
  preamble?: string;
  preludeSdkKey?: string;
  reCaptchaSiteKey?: string;
  title?: string;
  translations?: ProvetTranslations;
  treatmentNotes?: string;
  visitedClinicBookingProvetId?: number;
  visitedTreatmentProvetId?: number;
}

export const ProvetLayout = ({
  activeProvetDepartmentIds,
  advancedTrackingEnabled,
  allowTestErrorBoundary = false,
  antiForgeryToken,
  bookingInformation,
  defaultLatitude,
  defaultLongitude,
  defaultSpecies,
  defaultZoom,
  emergencyClinicsUrl,
  excludedProvetDepartmentIds,
  mapboxAccessToken,
  language,
  oldBookingDomain,
  preamble,
  preludeSdkKey,
  reCaptchaSiteKey,
  title,
  translations,
  treatmentNotes,
  visitedClinicBookingProvetId,
  visitedTreatmentProvetId
}: ProvetLayoutProps) => {
  // Outside production only: ?testErrorBoundary=true crashes the form on purpose
  const testError =
    allowTestErrorBoundary &&
    typeof window !== "undefined" &&
    new URLSearchParams(window.location.search).get("testErrorBoundary") === "true";

  const bookingSessionId = useMemo(() => getOrCreateBookingSessionId(), []);

  // React 17 doesn't pass errors caught by an error boundary to window.onerror, so they
  // are reported to Application Insights here. No personal data: only the error and the
  // component stack.
  const reportRenderError = useCallback(
    (error: Error, errorInfo: ErrorInfo) => {
      trackAppInsightsException(error, {
        Operation: "Render",
        Flow: "Provet",
        BookingSessionId: bookingSessionId,
        ComponentStack: errorInfo.componentStack?.trim().slice(0, 2000)
      });
    },
    [bookingSessionId]
  );

  return (
    <ErrorBoundary
      translations={{
        errorSomethingWentWrong: translations?.errorSomethingWentWrong,
        errorUnexpected: translations?.errorUnexpected,
        errorReloadPage: translations?.errorReloadPage
      }}
      onError={reportRenderError}
    >
      {/* Outside production: test the ErrorBoundary by adding ?testErrorBoundary=true to the URL */}
      {testError && (
        <ThrowError
          shouldThrow={true}
          errorMessage="Testing ErrorBoundary! (Remove ?testErrorBoundary=true from URL to continue)"
        />
      )}

      <ProvetContextProvider
        sessionId={bookingSessionId}
        antiForgeryToken={antiForgeryToken}
        activeDepartments={activeProvetDepartmentIds}
        excludedProvetDepartments={excludedProvetDepartmentIds}
        visitedClinicBookingProvetId={visitedClinicBookingProvetId}
        visitedTreatmentProvetId={visitedTreatmentProvetId}
        language={language}
      >
        <TranslationContextProvider translations={translations}>
          <ProvetForm
            activeProvetDepartmentIds={activeProvetDepartmentIds}
            advancedTrackingEnabled={advancedTrackingEnabled}
            bookingInformation={bookingInformation}
            defaultLatitude={defaultLatitude}
            defaultLongitude={defaultLongitude}
            defaultSpecies={defaultSpecies}
            defaultZoom={defaultZoom}
            emergencyClinicsUrl={emergencyClinicsUrl}
            excludedProvetDepartmentIds={excludedProvetDepartmentIds}
            mapboxAccessToken={mapboxAccessToken}
            oldBookingDomain={oldBookingDomain}
            preamble={preamble}
            preludeSdkKey={preludeSdkKey}
            reCaptchaSiteKey={reCaptchaSiteKey}
            sessionId={bookingSessionId}
            title={title}
            treatmentNotes={treatmentNotes}
          />
        </TranslationContextProvider>
      </ProvetContextProvider>
    </ErrorBoundary>
  );
};

export default ProvetLayout;
